import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Coins, Loader2, Check, Gift, Crown, Plus, Sparkles } from "lucide-react";
import { useCheckout } from "@/hooks/useCheckout";

/**
 * Unified Stripe checkout.
 * mode="subscription": a plan + optional add-on credit packs (bonus-priced for brand-new subscribers).
 * mode="credits": credit packs + an optional monthly-subscription upsell.
 */
export function CheckoutDialog({ open, onClose, mode = "credits", planId = null, interval = "month" }) {
  const {
    opts, loading, redirecting, selectedPacks, selectedPlan, setSelectedPlan,
    billingInterval, setBillingInterval, yearly, yearlyDiscountPct,
    isSub, isNew, useBonus, packs, plan, togglePack, chosenPacks, welcomeBonus,
    createathonApplies, createathonSale,
    total, totalCredits, proceed,
  } = useCheckout({ open, mode, planId, initialInterval: interval });

  const money = (n) => (Number.isInteger(Number(n)) ? String(n) : Number(n).toFixed(2));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121212] border-white/10 text-white sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="checkout-dialog">
        <DialogHeader>
          <DialogTitle className="font-head text-2xl flex items-center gap-2">
            {isSub ? <Crown className="h-5 w-5 text-blue-400" /> : <Coins className="h-5 w-5 text-blue-400" />}
            {isSub ? `Subscribe — ${plan?.name || ""}` : "Buy credits"}
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            {isSub ? "Confirm your plan and optionally bundle in extra credits." : "Top up credits — and optionally add a monthly plan."}
          </DialogDescription>
        </DialogHeader>

        {loading && !opts ? (
          <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-blue-400" /></div>
        ) : (
          <div className="space-y-4">
            {/* Subscription summary (subscription mode) */}
            {isSub && plan && (
              <div className="rounded-md border border-blue-500/30 bg-blue-600/10 p-4" data-testid="checkout-plan-summary">
                <div className="flex items-center justify-between">
                  <span className="font-head font-semibold">{plan.name} plan</span>
                  {createathonApplies ? (
                    <span className="font-head text-lg font-bold" data-testid="createathon-price">
                      <s className="text-zinc-500 text-sm mr-1.5">${money(plan.price)}</s>
                      ${money(createathonSale)}<span className="text-xs text-zinc-400"> first mo</span>
                    </span>
                  ) : (
                    <span className="font-head text-lg font-bold">${money(yearly ? plan.price_yearly : plan.price)}<span className="text-xs text-zinc-400">{yearly ? "/yr" : "/mo"}</span></span>
                  )}
                </div>
                <p className="text-sm text-zinc-400 mt-1 flex items-center gap-1.5"><Coins className="h-3.5 w-3.5 text-blue-400" /> {plan.credits.toLocaleString()} credits / month{yearly ? " · billed yearly" : ""}</p>
                {createathonApplies && (
                  <p className="text-sm text-fuchsia-300 mt-1.5 flex items-center gap-1.5" data-testid="createathon-checkout-note">
                    <Sparkles className="h-3.5 w-3.5" /> Video Create-a-thon: 20% off your first month (then ${money(plan.price)}/mo)
                  </p>
                )}
                {isNew && welcomeBonus > 0 && (
                  <p className="text-sm text-emerald-300 mt-1.5 flex items-center gap-1.5" data-testid="welcome-bonus-note">
                    <Gift className="h-3.5 w-3.5" /> +{welcomeBonus.toLocaleString()} free welcome credits (first subscription)
                  </p>
                )}
              </div>
            )}

            {/* Optional subscription upsell (credits mode) */}
            {!isSub && (
              <div data-testid="sub-upsell">
                <p className="text-xs uppercase tracking-[0.15em] text-zinc-500 mb-1.5 flex items-center gap-1.5"><Crown className="h-3.5 w-3.5 text-blue-400" /> Add a monthly subscription (optional)</p>
                <select
                  value={selectedPlan || ""}
                  onChange={(e) => setSelectedPlan(e.target.value || null)}
                  data-testid="upsell-plan-select"
                  className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">No subscription — just credits</option>
                  {(opts?.plans || []).map((p) => (
                    <option key={p.id} value={p.id}>{p.name} — ${p.price}/mo · {p.credits.toLocaleString()} credits</option>
                  ))}
                </select>
                {selectedPlan && isNew && welcomeBonus > 0 && (
                  <p className="text-sm text-emerald-300 mt-1.5 flex items-center gap-1.5" data-testid="welcome-bonus-note">
                    <Gift className="h-3.5 w-3.5" /> +{welcomeBonus.toLocaleString()} free welcome credits (first subscription)
                  </p>
                )}
              </div>
            )}

            {/* Monthly vs yearly billing toggle (only when a plan is selected) */}
            {plan && (
              <div data-testid="billing-interval-toggle">
                <p className="text-xs uppercase tracking-[0.15em] text-zinc-500 mb-2">Billing cycle</p>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setBillingInterval("month")}
                    data-testid="interval-month"
                    className={`text-left rounded-md p-3 border transition-colors ${!yearly ? "border-blue-500 bg-blue-600/15" : "border-white/10 bg-[#0A0A0A] hover:bg-white/5"}`}
                  >
                    <p className="font-head font-semibold text-sm">Monthly</p>
                    <p className="font-head text-xl font-black">${money(plan.price)}<span className="text-xs text-zinc-400 font-normal">/mo</span></p>
                    <p className="text-[11px] text-zinc-500">${money(plan.price * 12)}/yr total</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setBillingInterval("year")}
                    data-testid="interval-year"
                    className={`relative text-left rounded-md p-3 border transition-colors ${yearly ? "border-emerald-500 bg-emerald-600/15" : "border-white/10 bg-[#0A0A0A] hover:bg-white/5"}`}
                  >
                    <span className="absolute top-2 right-2 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">Save {yearlyDiscountPct}%</span>
                    <p className="font-head font-semibold text-sm">Yearly</p>
                    <p className="font-head text-xl font-black">${money(plan.price_yearly)}<span className="text-xs text-zinc-400 font-normal">/yr</span></p>
                    <p className="text-[11px] text-emerald-400/80">${(plan.price_yearly / 12).toFixed(2)}/mo billed yearly</p>
                  </button>
                </div>
              </div>
            )}

            {/* Credit packs */}
            <div>
              <p className="text-xs uppercase tracking-[0.15em] text-zinc-500 mb-2 flex items-center gap-1.5">
                <Plus className="h-3.5 w-3.5" /> {isSub ? "Add extra credits (optional)" : "Choose credit packs"}
                {useBonus && <span className="text-emerald-400 normal-case tracking-normal">· bonus pricing</span>}
              </p>
              <div className="grid grid-cols-2 gap-3">
                {packs.map((pkg) => {
                  const sel = selectedPacks.includes(pkg.id);
                  return (
                    <button
                      key={pkg.id}
                      onClick={() => togglePack(pkg.id)}
                      data-testid={`pack-${pkg.id}`}
                      className={`relative text-left rounded-md p-4 border transition-colors ${sel ? "border-blue-500 bg-blue-600/15" : "border-white/10 bg-[#0A0A0A] hover:bg-white/5"}`}
                    >
                      {sel && <Check className="absolute top-3 right-3 h-4 w-4 text-blue-400" />}
                      <p className="font-head text-base font-semibold">{pkg.name}</p>
                      <p className="font-head text-xl font-black flex items-center gap-1.5 mt-1">
                        <Coins className="h-4 w-4 text-blue-400" /> {pkg.credits.toLocaleString()}
                      </p>
                      {useBonus && pkg.regular_credits && (
                        <p className="text-[11px] text-emerald-400">+{(pkg.credits - pkg.regular_credits).toLocaleString()} bonus</p>
                      )}
                      <p className="text-sm text-zinc-400 mt-1">${pkg.price}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Total */}
            <div className="rounded-md border border-white/10 bg-[#0A0A0A] p-4 flex items-center justify-between" data-testid="checkout-total">
              <div>
                <p className="text-sm text-zinc-400">Total credits</p>
                <p className="font-head text-lg font-bold flex items-center gap-1.5"><Coins className="h-4 w-4 text-blue-400" /> {totalCredits.toLocaleString()}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-zinc-400">Pay today</p>
                <p className="font-head text-2xl font-black" data-testid="checkout-amount">${money(total)}</p>
                {plan && (
                  <p className="text-[11px] text-zinc-500 mt-0.5" data-testid="checkout-recurring-note">then ${money(yearly ? plan.price_yearly : plan.price)}/{yearly ? "year" : "month"}</p>
                )}
              </div>
            </div>

            <button
              onClick={proceed}
              disabled={redirecting || (!plan && chosenPacks.length === 0)}
              data-testid="checkout-proceed-btn"
              className="w-full bg-blue-600 hover:bg-blue-500 py-3 rounded-md font-semibold transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {redirecting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Proceed to secure checkout — ${money(total)}
            </button>
            <p className="text-xs text-zinc-600 text-center">
              Secure checkout by Stripe. {plan ? `Your plan rebills ${yearly ? "annually" : "monthly"} and can be canceled anytime.` : "One-time credit purchase."}
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
